import { ApiError } from '@kulak91/everbeat-api-client';
import { AxiosError, HttpStatusCode } from 'axios';
import { HttpError, type ErrorResponse } from '~/lib/exceptions';

type Success<T> = {
  data: T;
  error: null;
};

type Failure<E extends ErrorResponse> = {
  data: null;
  error: E;
};

type ApiResult<T> = Promise<Success<T> | Failure<ErrorResponse>>;

const tryCatch = async <T>(promise: Promise<T>): ApiResult<T> => {
  try {
    const data = await promise;

    return { data, error: null };
  } catch (error) {
    if (error instanceof HttpError && error.details) {
      return { data: null, error: error.details };
    }

    if (error instanceof ApiError) {
      const errorResponse: ErrorResponse = {
        message: (error.body as { detail: string })?.detail ?? error?.message,
        status: error.status,
        details: {
          url: error.url,
        },
      };

      return { data: null, error: errorResponse };
    }
    if (error instanceof AxiosError) {
      const errorResponse: ErrorResponse = {
        message: error.message,
        code: error.status,
        status: error.code as unknown as HttpStatusCode,
        details: { errorData: error.response?.data, url: error.config?.url },
      };

      return { data: null, error: errorResponse };
    }

    if (error instanceof Error && error.name === 'AbortError') {
      const errorResponse: ErrorResponse = {
        message: error.message,
        status: HttpStatusCode.TooEarly,
      };

      return { data: null, error: errorResponse };
    }

    if (error instanceof Error) {
      const errorResponse: ErrorResponse = {
        message: error.message,
      };

      return { data: null, error: errorResponse };
    }

    throw error;
  }
};

export { type ApiResult, tryCatch };
